import type { OpenImRawUser } from '../appDto/types';

import { mapOpenImUserToApiUser } from '../appDto/user';
import { callOpenIm } from '../worker/connector';

let operationCounter = 0;
function nextOperationId(): string {
  operationCounter += 1;
  return `web-friend-op-${operationCounter}`;
}

// `Relation.GetFriendListPage`'s `count` param (openim-sdk-core `internal/relation/api.go:188`);
// a generous single page since this app has no paging UI yet for the contact list.
const FRIEND_PAGE_SIZE = 200;

// Argument order/types mirror `open_im_sdk.GetFriendListPage(callback, operationID string, offset
// int32, count int32, filterBlack bool)` (openim-sdk-core `open_im_sdk/relation.go:27`) exactly —
// verified against the Go source, not just the wire-format guess this app started with.
export async function fetchOpenImFriendList() {
  const raw = await callOpenIm<string>('getFriendListPage', nextOperationId(), 0, FRIEND_PAGE_SIZE, false);
  const friends: OpenImRawUser[] = JSON.parse(raw);
  // Not a bare `friends.map(mapOpenImUserToApiUser)` -- `.map()` also passes the array index as a
  // second argument, which would land in `mapOpenImUserToApiUser`'s optional `isContact` parameter.
  return friends.map((friend) => mapOpenImUserToApiUser(friend));
}

// `open_im_sdk.GetSelfUserInfo(callback, operationID string)` (openim-sdk-core
// `open_im_sdk/user.go:31`) takes no arguments beyond the operationID, and its Go-side result is a
// single `*model_struct.LocalUser` (not an array) — `internal/user/api.go:33` — so the resolved
// JSON is a single object, unlike `fetchOpenImFriendList`'s array.
export async function fetchOpenImSelfUser(): Promise<ReturnType<typeof mapOpenImUserToApiUser>> {
  const raw = await callOpenIm<string>('getSelfUserInfo', nextOperationId());
  return mapOpenImUserToApiUser(JSON.parse(raw));
}

export async function addOpenImFriend(toUserId: string, reqMsg: string): Promise<void> {
  await callOpenIm('addFriend', nextOperationId(), JSON.stringify({ toUserID: toUserId, reqMsg }));
}

export async function deleteOpenImFriend(friendUserId: string): Promise<void> {
  await callOpenIm('deleteFriend', nextOperationId(), friendUserId);
}

// `open_im_sdk.AddBlack(callback, operationID string, blackUserID string, ex string)`
// (openim-sdk-core `open_im_sdk/relation.go:67`) -- `ex` is an opaque extra-info string, left blank
// (same "no reason/extra field needed" convention as `addOpenImFriend`'s `reqMsg`).
export async function blockOpenImUser(userId: string): Promise<void> {
  await callOpenIm('addBlack', nextOperationId(), userId, '');
}

// `open_im_sdk.RemoveBlack(callback, operationID string, removeUserID string)`
// (openim-sdk-core `open_im_sdk/relation.go:75`).
export async function unblockOpenImUser(userId: string): Promise<void> {
  await callOpenIm('removeBlack', nextOperationId(), userId);
}

// `open_im_sdk.GetBlackList(callback, operationID string)` → `[]*model_struct.LocalBlack`
// (json: `blockUserID` / `nickname` / `faceURL`).
export async function fetchOpenImBlackList() {
  const raw = await callOpenIm<string>('getBlackList', nextOperationId());
  const blacks: Array<{ blockUserID?: string; nickname?: string; faceURL?: string }> = JSON.parse(raw || '[]');
  return blacks
    .filter((entry) => Boolean(entry.blockUserID))
    .map((entry) => mapOpenImUserToApiUser({
      userID: entry.blockUserID!,
      nickname: entry.nickname || entry.blockUserID!,
      faceURL: entry.faceURL || '',
    }, false));
}